import { createCaveError, isTerminalCaveApiError } from './errors';
import { createThrowIfAborted } from './abort';

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const DEFAULT_INTERVAL_MS = 2000;

// Validates the shared global budget before an operation begins.
export const assertGlobalDeadline = (globalDeadline, operationName) => {
  if (!Number.isFinite(globalDeadline)) {
    throw new Error(`Global deadline is required for ${operationName}.`);
  }
  if (globalDeadline - Date.now() <= 0) {
    throw new Error(
      `Global budget exhausted before ${operationName} could begin.`,
    );
  }
};

// Runs `fn` and retries it on transient CAVE API errors until it succeeds, the
// shared global deadline passes, or the caller aborts. Terminal API errors
// (see isTerminalCaveApiError) are rethrown immediately without retry. Returns
// the first successful (non-throwing) result of `fn` untouched — response shape
// validation stays the caller's responsibility so it remains non-retriable.
export const withRetry = async (
  fn,
  {
    globalDeadline,
    shouldAbort,
    intervalMs = DEFAULT_INTERVAL_MS,
    errorPrefix,
    fallbackDetail,
    operationName = 'operation',
    timeoutMessage = `Timed out waiting for ${operationName}.`,
  } = {},
) => {
  assertGlobalDeadline(globalDeadline, operationName);

  const deadline = globalDeadline;
  const throwIfAborted = createThrowIfAborted(shouldAbort);

  let lastError;

  while (Date.now() <= deadline) {
    throwIfAborted();

    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (error) {
      lastError = createCaveError(error, {
        prefix: errorPrefix,
        fallbackDetail,
      });

      if (isTerminalCaveApiError(error)) {
        throw lastError;
      }

      throwIfAborted();

      // eslint-disable-next-line no-await-in-loop
      await sleep(Math.min(intervalMs, deadline - Date.now()));
    }
  }

  if (lastError) {
    throw lastError;
  }

  throw new Error(timeoutMessage);
};

export default withRetry;
